// src/services/rateLimitedAuthService.ts
import { logger } from '../utils/logger';
// Server-side rate-limited authentication service
// Provides protection against brute force attacks on auth endpoints

import { supabase } from './supabase';
import type { Session, User } from '@supabase/supabase-js';

// Circuit breaker: after FAILURE_THRESHOLD consecutive Edge Function failures within
// FAILURE_WINDOW_MS, the circuit opens and the unrate-limited fallback is blocked for
// COOLDOWN_MS. This prevents an attacker from triggering network errors to bypass
// the server-side rate limiter.
const FAILURE_THRESHOLD = 3;
const FAILURE_WINDOW_MS = 5 * 60 * 1000;  // 5 minutes
const COOLDOWN_MS = 10 * 60 * 1000;        // 10 minutes

const circuit = {
  failures: 0,
  windowStart: 0,
  openedAt: 0,

  recordFailure(): void {
    const now = Date.now();
    if (now - this.windowStart > FAILURE_WINDOW_MS) {
      this.failures = 0;
      this.windowStart = now;
    }
    this.failures += 1;
    if (this.failures >= FAILURE_THRESHOLD) {
      this.openedAt = now;
      logger.warn('Auth circuit breaker OPEN — fallback disabled for 10 minutes');
    }
  },

  isOpen(): boolean {
    if (this.failures < FAILURE_THRESHOLD) return false;
    if (Date.now() - this.openedAt >= COOLDOWN_MS) {
      // Cooldown elapsed — reset and allow one attempt through
      this.failures = 0;
      this.windowStart = 0;
      this.openedAt = 0;
      logger.log('Auth circuit breaker CLOSED — resuming normal operation');
      return false;
    }
    return true;
  },
};

interface AuthResponse {
  session?: Session | null;
  user?: User | null;
  error?: string;
  blocked?: boolean;
  blockedUntil?: string;
}

/**
 * Login with server-side rate limiting (5 attempts per 15 minutes)
 * Protects against brute force attacks by tracking IP address
 * Falls back to direct Supabase auth if Edge Function is not available
 */
export async function rateLimitedLogin(
  email: string,
  password: string
): Promise<AuthResponse> {
  try {
    const { data, error } = await supabase.functions.invoke('auth-login', {
      body: { email, password },
    });

    if (error) {
      logger.error('Login error from edge function:', error);

      // If Edge Function is not available, fall back to direct Supabase auth
      if (error.message && (
        error.message.includes('Failed to send') ||
        error.message.includes('FunctionsHttpError') ||
        error.message.includes('FunctionsRelayError') ||
        error.message.includes('fetch')
      )) {
        circuit.recordFailure();
        if (circuit.isOpen()) {
          return { error: 'Authentication service is temporarily unavailable. Please try again in a few minutes.' };
        }
        logger.warn('Edge Function unavailable, falling back to direct Supabase auth (no rate limiting)');
        return await directLogin(email, password);
      }

      return { error: error.message || 'Login failed' };
    }

    if (data.error) {
      return { error: data.error, blocked: data.blocked, blockedUntil: data.blockedUntil };
    }

    // Set the session in the Supabase client
    if (data.session) {
      await supabase.auth.setSession(data.session);
    }

    return {
      session: data.session,
      user: data.user,
    };
  } catch (error: any) {
    logger.error('Rate-limited login exception:', error);

    circuit.recordFailure();
    if (circuit.isOpen()) {
      return { error: 'Authentication service is temporarily unavailable. Please try again in a few minutes.' };
    }
    logger.warn('Edge Function invocation failed, falling back to direct Supabase auth (no rate limiting)');
    return await directLogin(email, password);
  }
}

/**
 * Direct login fallback (no rate limiting)
 * Used when Edge Functions are not available
 */
async function directLogin(
  email: string,
  password: string
): Promise<AuthResponse> {
  try {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      return { error: error.message };
    }

    return {
      session: data.session,
      user: data.user,
    };
  } catch (error: any) {
    return { error: error.message || 'Network error during login' };
  }
}

/**
 * Signup with server-side rate limiting (3 attempts per hour)
 * Prevents spam signups by tracking IP address
 * Falls back to direct Supabase auth if Edge Function is not available
 */
export async function rateLimitedSignup(
  email: string,
  password: string,
  username: string,
  emailRedirectTo: string
): Promise<AuthResponse> {
  try {
    const { data, error } = await supabase.functions.invoke('auth-signup', {
      body: {
        email,
        password,
        username,
        emailRedirectTo,
      },
    });

    if (error) {
      logger.error('Signup error from edge function:', error);

      // If Edge Function is not available, fall back to direct Supabase auth
      if (error.message && (
        error.message.includes('Failed to send') ||
        error.message.includes('FunctionsHttpError') ||
        error.message.includes('FunctionsRelayError') ||
        error.message.includes('fetch')
      )) {
        circuit.recordFailure();
        if (circuit.isOpen()) {
          return { error: 'Authentication service is temporarily unavailable. Please try again in a few minutes.' };
        }
        logger.warn('Edge Function unavailable, falling back to direct Supabase auth (no rate limiting)');
        return await directSignup(email, password, username, emailRedirectTo);
      }

      return { error: error.message || 'Signup failed' };
    }

    if (data.error) {
      return { error: data.error, blocked: data.blocked, blockedUntil: data.blockedUntil };
    }

    // Set the session if returned (for auto-login after signup)
    if (data.session) {
      await supabase.auth.setSession(data.session);
    }

    return {
      user: data.user,
      session: data.session,
    };
  } catch (error: any) {
    logger.error('Rate-limited signup exception:', error);

    circuit.recordFailure();
    if (circuit.isOpen()) {
      return { error: 'Authentication service is temporarily unavailable. Please try again in a few minutes.' };
    }
    logger.warn('Edge Function invocation failed, falling back to direct Supabase auth (no rate limiting)');
    return await directSignup(email, password, username, emailRedirectTo);
  }
}

/**
 * Direct signup fallback (no rate limiting)
 * Used when Edge Functions are not available
 */
async function directSignup(
  email: string,
  password: string,
  username: string,
  emailRedirectTo: string
): Promise<AuthResponse> {
  try {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          username: username,
        },
        emailRedirectTo: emailRedirectTo,
      },
    });

    if (error) {
      return { error: error.message };
    }

    return {
      user: data.user,
      session: data.session,
    };
  } catch (error: any) {
    return { error: error.message || 'Network error during signup' };
  }
}

/**
 * Password reset (already rate-limited in existing rate-limiter Edge Function)
 * Uses existing Supabase auth method since rate limiting is handled by rate-limiter function
 */
export async function rateLimitedPasswordReset(
  email: string,
  redirectTo: string
): Promise<{ error?: string }> {
  try {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo,
    });

    if (error) {
      return { error: error.message };
    }

    return {};
  } catch (error: any) {
    logger.error('Password reset error:', error);
    return { error: error.message || 'Network error during password reset' };
  }
}
